<template>
  <el-button
    v-if="shopAccess"
    class="add-to-cart"
    :size="size"
    :icon="ShoppingCart"
    :loading="pending"
    :disabled="isOff"
    :title="unavailableReason"
    @click.stop="onClick"
  >
    {{ buttonLabel }}
  </el-button>
</template>

<script setup>
import { ShoppingCart } from "@element-plus/icons-vue";

const props = defineProps({
  productId: { type: [Number, String], required: true },
  colorId: { type: [Number, String], default: null },
  disabled: { type: Boolean, default: false },
  // Цвета нет на складе, а товар не производится — купить нечего
  soldOut: { type: Boolean, default: false },
  // Заполнены ли вес и габариты: без них доставку не рассчитать
  orderable: { type: Boolean, default: true },
  // У товара есть вариации цвета — без явного выбора в корзину не кладём
  colorRequired: { type: Boolean, default: false },
  // Ожидаемая дата доставки. Когда задана, встаёт вместо надписи
  // «В корзину» — как на карточках маркетплейсов
  deliveryLabel: { type: String, default: "" },
  deliveryHint: { type: String, default: "" },
  quantity: { type: Number, default: 1 },
  size: { type: String, default: "default" },
});

// Родитель подсвечивает выбор цвета: кнопка знает только о самом факте
const emit = defineEmits(["color-required"]);

const shopAccess = useShopAccess();
const { add, pending } = useCart();

const isOff = computed(() => props.disabled || props.soldOut || !props.orderable);

const buttonLabel = computed(() => {
  if (props.disabled) return "Недоступен";
  if (props.soldOut) return "Нет в наличии";
  // Покупателю ни к чему знать про незаполненные габариты — для него это
  // просто недоступная доставка
  if (!props.orderable) return "Доставка временно недоступна";
  // Срок известен не всегда: не выбран город, СДЭК не ответил
  return props.deliveryLabel || "В корзину";
});

const unavailableReason = computed(() => {
  if (props.disabled) return "Товар временно недоступен";
  if (props.soldOut) return "На складе нет, а под заказ товар не изготавливается";
  if (!props.orderable) return "У товара не указаны вес и габариты — доставку рассчитать нельзя";
  // Когда на кнопке дата, подсказка расшифровывает, из чего она сложилась
  return props.deliveryHint;
});

// @click.stop в шаблоне обязателен: в ProductsList переход на товар висит
// на контейнерном обработчике карточки, иначе добавление уводило бы со страницы
const onClick = async () => {
  const colorId =
    props.colorId === null || props.colorId === undefined || props.colorId === ""
      ? null
      : parseInt(props.colorId, 10);

  // Раньше подставлялся первый цвет товара — покупатель мог не заметить,
  // что заказал не тот, который смотрел
  if (props.colorRequired && colorId === null) {
    ElMessage.warning("Выберите цвет");
    emit("color-required");
    return;
  }

  await add(parseInt(props.productId, 10), colorId, props.quantity);
};
</script>

<style scoped>
/* Селектор с .el-button, а не один класс: у Element Plus собственные
   правила на .el-button задают эти же переменные и с одним классом
   перебивают наши по специфичности */
.el-button.add-to-cart {
  /* Доступна — фирменный зелёный. Значения ровно те, что Element Plus
     генерирует для color="#008001" на кнопке «Найти» в каталоге,
     чтобы кнопки на сайте выглядели одинаково */
  --el-button-bg-color: #008001;
  --el-button-border-color: #008001;
  --el-button-text-color: var(--el-color-white);
  --el-button-hover-bg-color: #4da64d;
  --el-button-hover-border-color: #4da64d;
  --el-button-hover-text-color: var(--el-color-white);
  --el-button-active-bg-color: #046a05;
  --el-button-active-border-color: #046a05;
  --el-button-active-text-color: var(--el-color-white);

  /* Белым по зелёному тонкий шрифт читается хуже, чем тёмным по светлому —
     поэтому надпись плотнее обычной кнопки */
  font-weight: 600;

  /* Недоступна — нейтральный серый. Прописан здесь, а не через проп color:
     тот подмешал бы в неактивное состояние зелёный оттенок */
  --el-button-disabled-bg-color: var(--el-fill-color-light);
  --el-button-disabled-border-color: var(--el-border-color-light);
  --el-button-disabled-text-color: var(--el-text-color-placeholder);
}

/* Текст недоступного состояния длиннее «В корзину» и в узкой карточке
   каталога в строку не помещается — разрешаем перенос. min-height по размеру
   кнопки: пока текст в одну строку, высота совпадает с доступным состоянием */
.el-button.add-to-cart.is-disabled {
  height: auto;
  min-height: var(--el-component-size);
  padding-block: 2px;
  white-space: normal;
  line-height: 1.25;
}

.el-button.add-to-cart.el-button--small.is-disabled {
  min-height: var(--el-component-size-small);
}

.el-button.add-to-cart.el-button--large.is-disabled {
  min-height: var(--el-component-size-large);
}
</style>
